import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tab, { TabProps } from "@mui/material/Tab";
import Tabs, { TabsProps } from "@mui/material/Tabs";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import LinearProgress from "@mui/material/LinearProgress";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { useStoreDispatch, useStoreSelector } from "../store/Hooks";
import {
  navSliceActions,
  setNavigationHistoryFromURL,
} from "../store/slices/NavSlice";
import { interactionTreeSliceActions } from "../store/slices/InteractionTreeSlice";
import {
  doesURLTriggerPreview,
  parseInteractionRouteParams,
} from "../models/interactions/InteractionRouteParam";
import InteractionNode from "../models/interactions/InteractionNode";
import Contents from "../components/pathwaytabs/Contents";
import Patients from "../components/pathwaytabs/Patients";
import Reports from "../components/pathwaytabs/Reports";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import Interaction from "../models/interactions/Interaction";

const StyledTabs = styled((props: TabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    style={{ height: "50px" }}
  />
))({
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    maxWidth: 50,
    width: "100%",
    backgroundColor: "#635ee7",
  },
});

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    color: "black",
    width: "75px",
    "&.Mui-selected": {
      fontWeight: theme.typography.fontWeightBold,
      color: "#1976d2",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "rgba(100, 95, 228, 0.92)",
    },
  }),
);

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const PathwayPage = () => {
  const dispatch = useStoreDispatch();
  const { interactionPath } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentTab = useStoreSelector((state) => state.nav.currentTab);
  const previewMode = useStoreSelector((state) => state.nav.previewMode);

  const rootInteractionID =   useStoreSelector((state) => state.interactionTree.RootInteractionID);
  const interactions =   useStoreSelector((state) => state.interactionTree.Interactions);
  const rootInteractionIndex = interactions.findIndex((interaction:Interaction)=>{
    return interaction.id == rootInteractionID;
  })
  const [initialLoad, setInitialLoad] = useState<boolean>(false);

  const rootInteraction = interactions[rootInteractionIndex];
  // hide patients/anchors tool unless we are on a pathway
  const showPatientsTab = rootInteraction && 
                          rootInteraction.properties && 
                          rootInteraction.properties.type == 'pathway' 
                            ? true : false;
  


  const {t} = useTranslation()

  useEffect(() => {
    if (typeof interactionPath !== "undefined") {
      // Reset loading states
      setInitialLoad(false);
      dispatch(interactionTreeSliceActions.updateLoaded(false));

      // TODO - GET THIS PLAYING NICELY WITH THE POSTGRES get_interactions_for_nav_history()
      // dispatch(setNavigationHistoryFromURL(params.interactionPath));
      dispatch(setNavigationHistoryFromURL(interactionPath));
      const paramsArray = parseInteractionRouteParams(interactionPath);
      // take the last interaction in the route is the one we need to show
      // (as last interaction in the route represents THIS interaction)
      const thisSet = paramsArray[paramsArray.length - 1].set;

      // in the url segement, p or parent is actually the child_interaction_id for the current node we are on
      // (its listed as a parent for when getting data we find children where "parent is this")
      const thisChildInteractionID = paramsArray[paramsArray.length - 1].parent;
      // this is the actual nodeID of this node
      // (its not always available)
      const thisNodeID = paramsArray[paramsArray.length - 1].nodeid;
      dispatch(interactionTreeSliceActions.updateRootInteractionID(thisSet));

      if (thisChildInteractionID !== null) {
        dispatch(
          interactionTreeSliceActions.updateCurrentRootInteractionID(
            thisChildInteractionID,
          ),
        );
      } else {
        dispatch(
          interactionTreeSliceActions.updateCurrentRootInteractionID(thisSet),
        );
      }

      // At this point some tabs can be displayed, such as patients and reports
      setInitialLoad(true);
      // wait 3 miliseconds to make sure nav history is set before attempting to load anything
      setTimeout(async () => {
        // if '+' indicator is the last char in the url,
        // the app is being accessed in preview mode (used for selecting existing interactions)
        // handle that here
        const previewMode = doesURLTriggerPreview(interactionPath);
        if (previewMode) {
          dispatch(navSliceActions.turnPreviewModeOn());
        } else {
          dispatch(navSliceActions.turnPreviewModeOff());
        }
        // let stateDataForDirectAncestor:InteractionNode|null = getStateDataForDirectAncestor(thisSet,thisParent)

        // attempt to get this interaction's node from nav history
        // this will first try to find this node via nav history using parent and set
        // then it will try nodeID if available
        // if it still can't find anything, it will try to use NodeID and query the db
        const thisNodeFromNavHistory: InteractionNode | null=
          await InteractionNode.findFromNavHistory(thisSet, thisChildInteractionID, thisNodeID);

        if (thisChildInteractionID !== null) {
          // the url doesnt know if an interaction is shared or not
          // so we should attempt to get all interaction nodes under the current set,
          // and then on top of that, APPEND any interaction nodes which have this node
          // as a parent but the parent's parent's set_interaction_id as the set
          // (2 total db calls)
          await Interaction.fetchAndProcessInteractions(
            thisSet, // set
            false, // do not append
            thisChildInteractionID, // parent
            false, // dont update breadcrumbs
            thisNodeFromNavHistory,
          );
          // manually delay this request to ensure it completes after the prior one
          setTimeout(async ()=>{
            // if the set in the URL (which we already tried) is different
            await Interaction.fetchAndProcessInteractions(
              thisSet == thisNodeFromNavHistory?.set_interaction_id ? thisNodeFromNavHistory.child_interaction_id : thisNodeFromNavHistory?.set_interaction_id  || 0,
              true,
              thisChildInteractionID
            )
          },500)
        } else {
          // the url doesnt know if an interaction is shared or not
          // so we should attempt to get all interaction nodes under the current set,
          // and then on top of that, APPEND any interaction nodes which have this node
          // as a parent but the parent's parent's set_interaction_id as the set
          // (2 total db calls)
          await Interaction.fetchAndProcessInteractions(
            thisSet,
            false,
            thisSet,
            false,
            thisNodeFromNavHistory,
          )
          // manually delay this request to ensure it completes after the prior one
          // if thisSet == thisNodeFromNavHistory.set_interaction.id, try
          setTimeout(async ()=>{
            await Interaction.fetchAndProcessInteractions(
              thisSet == thisNodeFromNavHistory?.set_interaction_id ? thisNodeFromNavHistory.child_interaction_id : thisNodeFromNavHistory?.set_interaction_id  || 0,
              true,
              thisChildInteractionID
            )
          },500)
        }
      }, 500);
    }
    const requestedTab = pathname.split("/")[1];
    dispatch(navSliceActions.updateTab(requestedTab));
  }, [interactionPath]);

  const handleChange = (event: React.SyntheticEvent, value: string) => {
    dispatch(navSliceActions.updateTab(value));
    navigate("/" + value + "/" + interactionPath);
  };

  if (previewMode) {
    return <Contents />;
  } else {
    return (
      <Box sx={{ width: "100%", color: "black" }}>
        <TabContext value={currentTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderRight: 1,
              borderColor: "divider",
              backgroundColor: "gainsboro",
            }}
          >
            <StyledTabs
              sx={{ borderRight: 1 }}
              value={currentTab}
              onChange={handleChange}
              aria-label="Caresense Nav Tabs"
            >
              <StyledTab
                label={t('content_tab_label')}
                {...a11yProps(0)}
                value={"contents"}
              />
              {/* <StyledTab
                label={"Reports"}
                {...a11yProps(1)}
                value={"reports"}
              /> */}
              {showPatientsTab && <StyledTab
                label={t('patients_tab_label')}
                {...a11yProps(1)}
                value={"patients"}
                sx={{visibility: showPatientsTab ? "visible" : "hidden"}}
              />}
              {/* Below is a placeholder tab, used while we load the current tab from url */}
              <StyledTab
                label={""}
                {...a11yProps(3)}
                value={""}
                sx={{ visibility: "hidden" }}
              />
            </StyledTabs>
          </Box>
          {/* Below is a placeholder tab panel, used while we load the current tab from url */}
          <TabPanel value={""}>
          <div style={{ minHeight: "62vh" }} ><LinearProgress /></div>
          </TabPanel>
          <TabPanel value={"contents"}>
            {!initialLoad && <div style={{ minHeight: "62vh" }} ><LinearProgress /></div>}
            {initialLoad && <Contents />}
          </TabPanel>
          {/* <TabPanel value={"reports"}>
            {!initialLoad && <div style={{ minHeight: "62vh" }} ><LinearProgress /></div>}
            {initialLoad && <Reports />}
          </TabPanel> */}
          {showPatientsTab && <TabPanel value={"patients"}>
            {!initialLoad && <div style={{ minHeight: "62vh" }} ><LinearProgress /></div>}
            {initialLoad && showPatientsTab && <Patients />}
          </TabPanel>}
        </TabContext>
      </Box>
    );
  }
};

export default PathwayPage;
